import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ProductCard from './ProductCard';
import api from '../../../../utils/api';
import { getImageUrl } from '../../../../utils/imageUrl';

// Same pastel backgrounds the carousel used before
const COLORS = ['#ffd8c4', '#d9e8ef', '#fff0b0', '#dce5dc'];
const CAROUSEL_SIZE = 4;

export default function Products({ copy }) {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [items, setItems] = useState([]);

  // Load real products (same endpoint /shop uses) and keep the first 4 that were added
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const response = await api.get('/products', { params: { sort: 'newest' } });
        const all = Array.isArray(response.data.data) ? response.data.data : [];

        // Oldest first = the first products that were added to the store
        const firstAdded = [...all]
          .sort((a, b) => Number(a.id) - Number(b.id))
          .slice(0, CAROUSEL_SIZE);

        if (!cancelled) setItems(firstAdded);
      } catch (error) {
        console.error('Error fetching carousel products:', error);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [i18n.language]);

  const cards = useMemo(
    () =>
      items.map((p, i) => ({
        id: p.id,
        name: p.name,
        subtitle: p.category_name || '',
        tag: p.category_name || '',
        price: `₾${parseFloat(p.price).toFixed(2)}`,
        image: p.primary_image ? getImageUrl(p.primary_image) : null,
        color: COLORS[i % COLORS.length],
        identifier: p.slug || p.id,
      })),
    [items]
  );

  // Duplicated so the CSS marquee loops seamlessly (padded so it is never too short)
  const looped = useMemo(() => {
    if (cards.length === 0) return [];
    let base = cards;
    while (base.length < CAROUSEL_SIZE) base = [...base, ...cards];
    return [...base, ...base];
  }, [cards]);

  const openProduct = (product) => {
    if (!product.identifier) return;
    navigate(`/product/${product.identifier}`);
  };

  return (
    <section className="kido-products-section" id="shop">
      <div className="kido-section-heading">
        <div>
          <p className="kido-eyebrow">01  /  {copy.shop}</p>
          <h2>{copy.collection}</h2>
          <p>{copy.collectionSub}</p>
        </div>
        <Link className="kido-text-link" to="/shop">{copy.seeAll} <span>↗</span></Link>
      </div>
      <div className="kido-carousel-window">
        <div className="kido-product-track">
          {looped.map((product, i) => (
            <ProductCard
              key={`${product.id}-${i}`}
              product={product}
              addLabel={copy.add}
              onAdd={() => openProduct(product)}
              onOpen={() => openProduct(product)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
